import { z } from 'zod';
import {
  FACULTY_LOAD_STATUS_VALUES,
  IMPORT_ROW_STATUS_VALUES,
  LOAD_TYPE_VALUES,
  ONLINE_PLATFORM_VALUES,
  SCHEDULE_EXCEPTION_TYPE_VALUES,
  SCHEDULE_STATUS_VALUES,
  SCHEDULED_MODALITY_VALUES,
} from '@fas/types';
import {
  booleanQuerySchema,
  isoDateSchema,
  optionalText,
  paginationQuerySchema,
  refineEffectiveRange,
  timeOfDaySchema,
  uuidSchema,
} from './common';

// ── Faculty loads ──────────────────────────────────────────────────────────────
const units = z.number().min(0).max(99);
const facultyLoadBase = z.object({
  facultyId: uuidSchema,
  termId: uuidSchema,
  courseSectionId: uuidSchema.nullable().optional(),
  loadType: z.enum(LOAD_TYPE_VALUES),
  units: units.nullable().optional(),
  contactHoursPerWeek: z.number().min(0).max(168).nullable().optional(),
  description: optionalText(500),
  status: z.enum(FACULTY_LOAD_STATUS_VALUES).optional(),
  remarks: optionalText(1000),
});

function refineLoad(v: { courseSectionId?: string | null; description?: string | null }, ctx: z.RefinementCtx) {
  if (!v.courseSectionId && !v.description) {
    ctx.addIssue({ code: 'custom', path: ['description'], message: 'Describe the load when it is not tied to a course section' });
  }
}

export const createFacultyLoadSchema = facultyLoadBase.superRefine(refineLoad);
export const updateFacultyLoadSchema = facultyLoadBase.omit({ facultyId: true, termId: true }).partial();
export type CreateFacultyLoadInput = z.infer<typeof createFacultyLoadSchema>;
export type UpdateFacultyLoadInput = z.infer<typeof updateFacultyLoadSchema>;

export const listFacultyLoadsQuerySchema = paginationQuerySchema.extend({
  termId: uuidSchema.optional(),
  facultyId: uuidSchema.optional(),
  departmentId: uuidSchema.optional(),
  loadType: z.enum(LOAD_TYPE_VALUES).optional(),
  status: z.enum(FACULTY_LOAD_STATUS_VALUES).optional(),
});
export type ListFacultyLoadsQuery = z.infer<typeof listFacultyLoadsQuerySchema>;

export const loadSummaryQuerySchema = z.object({
  termId: uuidSchema,
  departmentId: uuidSchema.optional(),
});
export type LoadSummaryQuery = z.infer<typeof loadSummaryQuerySchema>;

// ── Class schedules ────────────────────────────────────────────────────────────
const httpsUrl = z
  .string()
  .trim()
  .max(500)
  .transform((v) => (v.length === 0 ? null : v))
  .pipe(z.url({ protocol: /^https$/, error: 'Meeting link must be an https:// URL' }).nullable())
  .nullable()
  .optional();

const conflictAck = {
  /** Save despite blocking conflicts (requires a reason; audited). */
  acknowledgeConflicts: z.boolean().optional(),
  conflictReason: z.string().trim().max(500).optional(),
};

const scheduleBase = z.object({
  facultyId: uuidSchema,
  courseSectionId: uuidSchema,
  facultyLoadId: uuidSchema.nullable().optional(),
  dayOfWeek: z.number().int().min(1, 'Day must be 1 (Monday) to 7 (Sunday)').max(7, 'Day must be 1 (Monday) to 7 (Sunday)'),
  startTime: timeOfDaySchema,
  endTime: timeOfDaySchema,
  locationId: uuidSchema.nullable().optional(),
  scheduledModality: z.enum(SCHEDULED_MODALITY_VALUES),
  loadType: z.enum(LOAD_TYPE_VALUES),
  onlinePlatform: z.enum(ONLINE_PLATFORM_VALUES).nullable().optional(),
  meetingUrl: httpsUrl,
  effectiveFrom: isoDateSchema.optional(),
  effectiveTo: isoDateSchema.optional(),
  status: z.enum(['DRAFT', 'ACTIVE'] as const).optional(),
  remarks: optionalText(1000),
});

function refineSchedule(
  v: { startTime?: string; endTime?: string; effectiveFrom?: string | null; effectiveTo?: string | null; acknowledgeConflicts?: boolean; conflictReason?: string },
  ctx: z.RefinementCtx,
) {
  if (v.startTime && v.endTime && v.startTime === v.endTime) {
    ctx.addIssue({ code: 'custom', path: ['endTime'], message: 'End time must differ from start time' });
  }
  refineEffectiveRange(v, ctx);
  if (v.acknowledgeConflicts && (!v.conflictReason || v.conflictReason.length < 5)) {
    ctx.addIssue({ code: 'custom', path: ['conflictReason'], message: 'Give a reason (at least 5 characters) to override conflicts' });
  }
}

export const createScheduleSchema = scheduleBase.extend(conflictAck).superRefine(refineSchedule);
export const updateScheduleSchema = scheduleBase.partial().extend(conflictAck).superRefine(refineSchedule);
export type CreateScheduleInput = z.infer<typeof createScheduleSchema>;
export type UpdateScheduleInput = z.infer<typeof updateScheduleSchema>;

export const duplicateScheduleSchema = z
  .object({
    facultyId: uuidSchema.optional(),
    courseSectionId: uuidSchema.optional(),
    dayOfWeek: z.number().int().min(1).max(7).optional(),
    startTime: timeOfDaySchema.optional(),
    endTime: timeOfDaySchema.optional(),
    locationId: uuidSchema.nullable().optional(),
    effectiveFrom: isoDateSchema.optional(),
    effectiveTo: isoDateSchema.optional(),
    ...conflictAck,
  })
  .superRefine(refineSchedule);
export type DuplicateScheduleInput = z.infer<typeof duplicateScheduleSchema>;

export const conflictAckSchema = z.object(conflictAck).superRefine(refineSchedule);
export type ConflictAckInput = z.infer<typeof conflictAckSchema>;

export const deactivateScheduleSchema = z.object({
  reason: z.string().trim().min(3, 'Give a reason').max(500),
  /** Last date the schedule remains effective; defaults to keeping history intact. */
  effectiveTo: isoDateSchema.optional(),
});
export type DeactivateScheduleInput = z.infer<typeof deactivateScheduleSchema>;

export const listSchedulesQuerySchema = paginationQuerySchema.extend({
  termId: uuidSchema.optional(),
  facultyId: uuidSchema.optional(),
  departmentId: uuidSchema.optional(),
  collegeId: uuidSchema.optional(),
  courseSectionId: uuidSchema.optional(),
  locationId: uuidSchema.optional(),
  dayOfWeek: z.coerce.number().int().min(1).max(7).optional(),
  scheduledModality: z.enum(SCHEDULED_MODALITY_VALUES).optional(),
  loadType: z.enum(LOAD_TYPE_VALUES).optional(),
  status: z.enum(SCHEDULE_STATUS_VALUES).optional(),
  conflictOverride: booleanQuerySchema.optional(),
});
export type ListSchedulesQuery = z.infer<typeof listSchedulesQuerySchema>;

export const scheduleConflictsQuerySchema = z.object({
  termId: uuidSchema,
  departmentId: uuidSchema.optional(),
});
export type ScheduleConflictsQuery = z.infer<typeof scheduleConflictsQuerySchema>;

export const occurrencesQuerySchema = z
  .object({
    from: isoDateSchema,
    to: isoDateSchema,
    facultyId: uuidSchema.optional(),
  })
  .superRefine((v, ctx) => {
    if (v.to < v.from) ctx.addIssue({ code: 'custom', path: ['to'], message: 'Must be on or after "from"' });
    const days = (Date.parse(v.to) - Date.parse(v.from)) / 86_400_000;
    if (days > 62) ctx.addIssue({ code: 'custom', path: ['to'], message: 'Range cannot exceed 62 days' });
  });
export type OccurrencesQuery = z.infer<typeof occurrencesQuerySchema>;

export const todayQuerySchema = z.object({
  date: isoDateSchema.optional(),
  facultyId: uuidSchema.optional(),
});
export type TodayQuery = z.infer<typeof todayQuerySchema>;

// ── Schedule exceptions (§63: last-minute changes, room change, cancellation) ──
export const createScheduleExceptionSchema = z
  .object({
    exceptionDate: isoDateSchema,
    exceptionType: z.enum(SCHEDULE_EXCEPTION_TYPE_VALUES),
    newStartTime: timeOfDaySchema.nullable().optional(),
    newEndTime: timeOfDaySchema.nullable().optional(),
    newLocationId: uuidSchema.nullable().optional(),
    newModality: z.enum(SCHEDULED_MODALITY_VALUES).nullable().optional(),
    reason: z.string().trim().min(3, 'Give a reason').max(500),
  })
  .superRefine((v, ctx) => {
    if (v.exceptionType === 'RESCHEDULED') {
      if (!v.newStartTime) ctx.addIssue({ code: 'custom', path: ['newStartTime'], message: 'New start time is required' });
      if (!v.newEndTime) ctx.addIssue({ code: 'custom', path: ['newEndTime'], message: 'New end time is required' });
      if (v.newStartTime && v.newStartTime === v.newEndTime) {
        ctx.addIssue({ code: 'custom', path: ['newEndTime'], message: 'End time must differ from start time' });
      }
    }
    if (v.exceptionType === 'ROOM_CHANGE' && !v.newLocationId) {
      ctx.addIssue({ code: 'custom', path: ['newLocationId'], message: 'New location is required' });
    }
    if (v.exceptionType === 'MODALITY_CHANGE' && !v.newModality) {
      ctx.addIssue({ code: 'custom', path: ['newModality'], message: 'New modality is required' });
    }
  });
export type CreateScheduleExceptionInput = z.infer<typeof createScheduleExceptionSchema>;

// ── Import (§11.3, §73) ────────────────────────────────────────────────────────
/** Template columns in the order of SPEC §73. */
export const SCHEDULE_IMPORT_COLUMNS = [
  'employee_number',
  'academic_year',
  'term',
  'course_code',
  'section',
  'day_of_week',
  'start_time',
  'end_time',
  'campus',
  'building',
  'room',
  'scheduled_modality',
  'load_type',
  'effective_from',
  'effective_to',
] as const;
export type ScheduleImportColumn = (typeof SCHEDULE_IMPORT_COLUMNS)[number];

export const SCHEDULE_IMPORT_REQUIRED_COLUMNS: readonly ScheduleImportColumn[] = [
  'employee_number',
  'academic_year',
  'term',
  'course_code',
  'section',
  'day_of_week',
  'start_time',
  'end_time',
  'scheduled_modality',
];

export const updateImportRowSchema = z.object({
  /** Only the corrected columns need to be sent (Zod 4 enum-keyed records are exhaustive, hence partialRecord). */
  values: z.partialRecord(z.enum(SCHEDULE_IMPORT_COLUMNS), z.string().max(200)),
});
export type UpdateImportRowInput = z.infer<typeof updateImportRowSchema>;

export const skipImportRowSchema = z.object({ skip: z.boolean() });
export type SkipImportRowInput = z.infer<typeof skipImportRowSchema>;

export const listImportRowsQuerySchema = paginationQuerySchema.extend({
  status: z.enum(IMPORT_ROW_STATUS_VALUES).optional(),
});
export type ListImportRowsQuery = z.infer<typeof listImportRowsQuerySchema>;

export const importTemplateQuerySchema = z.object({ format: z.enum(['csv', 'xlsx']).default('xlsx') });
export type ImportTemplateQuery = z.infer<typeof importTemplateQuerySchema>;
